import { z } from 'zod';

/** Runtime configuration injected by the host page (`window.__ZAGRU_CONFIG__`, typically from `public/config.js`). */
export interface ZagruRuntimeConfig {
  /** Base URL of the API gateway. Empty string means same origin. */
  apiBaseUrl: string;
  oidc: {
    issuer: string;
    clientId: string;
    redirectUri: string;
    /** `prompt=none` redirect target for silent session restoration (ADR-0022). Empty disables the iframe. */
    silentRedirectUri?: string;
    postLogoutRedirectUri?: string;
    scopes?: string[];
    audience?: string;
  };
  telemetry?: {
    exporter?: 'none' | 'console' | 'otlp';
    otlpUrl?: string;
    serviceName?: string;
    sampleRate?: number;
  };
  mockApi?: boolean;
  /** Static feature flag overrides (win over server-provided flags). */
  features?: Record<string, boolean>;
  environment?: string;
  /** Product documentation, opened from the header's help menu and the Help page. Absent hides the link. */
  docsUrl?: string;
  /** Where users ask for support (a portal or `mailto:`), from the help menu and the Help page. Absent hides it. */
  supportUrl?: string;
}

declare global {
  interface Window {
    __ZAGRU_CONFIG__?: Partial<ZagruRuntimeConfig>;
  }
}

const oidcSchema = z
  .object({
    issuer: z.string().default(''),
    clientId: z.string().default(''),
    redirectUri: z.string().default(''),
    silentRedirectUri: z.string().optional(),
    postLogoutRedirectUri: z.string().optional(),
    scopes: z.array(z.string()).optional(),
    audience: z.string().optional(),
  })
  .loose();

const telemetrySchema = z
  .object({
    exporter: z.enum(['none', 'console', 'otlp']).optional(),
    otlpUrl: z.string().optional(),
    serviceName: z.string().optional(),
    sampleRate: z.number().min(0).max(1).optional(),
  })
  .loose();

/** Lenient schema: unknown keys are kept, missing keys fall back to defaults. */
export const runtimeConfigSchema = z
  .object({
    apiBaseUrl: z.string().default(''),
    oidc: oidcSchema.default({ issuer: '', clientId: '', redirectUri: '' }),
    telemetry: telemetrySchema.optional(),
    mockApi: z.boolean().optional(),
    features: z.record(z.string(), z.boolean()).optional(),
    environment: z.string().optional(),
    docsUrl: z.string().optional(),
    supportUrl: z.string().optional(),
  })
  .loose();

function originPath(path: string): string {
  if (typeof window !== 'undefined' && window.location) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

function defaultRedirectUri(): string {
  return originPath('/auth/callback');
}

/** Static page that hands the silent `prompt=none` answer back to the app (`public/auth/silent-callback.html`). */
function defaultSilentRedirectUri(): string {
  return originPath('/auth/silent-callback.html');
}

export function defaultRuntimeConfig(): ZagruRuntimeConfig {
  return {
    apiBaseUrl: '',
    oidc: {
      issuer: '',
      clientId: 'zagru-web',
      redirectUri: defaultRedirectUri(),
      silentRedirectUri: defaultSilentRedirectUri(),
      // No `offline_access`: a browser never holds an offline token (ADR-0022). The refresh token is bound to the
      // identity provider's SSO session, and a reload restores from that session silently.
      scopes: ['openid', 'profile', 'email'],
    },
    telemetry: { exporter: 'none' },
    mockApi: false,
    features: {},
    environment: 'development',
  };
}

let memoized: ZagruRuntimeConfig | null = null;
let programmatic: Partial<ZagruRuntimeConfig> | null = null;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function deepMerge<T>(base: T, override: unknown): T {
  if (override === undefined || override === null) return base;
  if (!isPlainObject(base) || !isPlainObject(override)) return override as T;
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (value === undefined) continue;
    const current = out[key];
    out[key] = isPlainObject(current) && isPlainObject(value) ? deepMerge(current, value) : value;
  }
  return out as T;
}

function readWindowConfig(): Partial<ZagruRuntimeConfig> | undefined {
  if (typeof window === 'undefined') return undefined;
  const raw = window.__ZAGRU_CONFIG__;
  return isPlainObject(raw) ? (raw as Partial<ZagruRuntimeConfig>) : undefined;
}

/**
 * Returns the effective runtime configuration: defaults ← window.__ZAGRU_CONFIG__ ← setRuntimeConfig().
 * The result is memoized until {@link setRuntimeConfig} or {@link resetRuntimeConfig} is called.
 * Validation is lenient: invalid values are reported via console.warn and replaced by defaults.
 */
export function getRuntimeConfig(): ZagruRuntimeConfig {
  if (memoized) return memoized;
  let merged = deepMerge(defaultRuntimeConfig(), readWindowConfig());
  merged = deepMerge(merged, programmatic);
  const parsed = runtimeConfigSchema.safeParse(merged);
  if (parsed.success) {
    memoized = parsed.data as ZagruRuntimeConfig;
  } else {
    console.warn('[zagru/platform] invalid runtime configuration, falling back to defaults', parsed.error.issues);
    memoized = deepMerge(defaultRuntimeConfig(), stripInvalid(merged as unknown as Record<string, unknown>, parsed.error));
  }
  return memoized;
}

function stripInvalid(value: Record<string, unknown>, error: z.ZodError): ZagruRuntimeConfig {
  const copy: Record<string, unknown> = structuredClone(value);
  for (const issue of error.issues) {
    const path = issue.path.map(String);
    if (path.length === 0) continue;
    let cursor: Record<string, unknown> = copy;
    for (let i = 0; i < path.length - 1; i += 1) {
      const next = cursor[path[i] ?? ''];
      if (!isPlainObject(next)) {
        cursor = {};
        break;
      }
      cursor = next;
    }
    delete cursor[path[path.length - 1] ?? ''];
  }
  return copy as unknown as ZagruRuntimeConfig;
}

/** Overrides configuration programmatically (tests, bootstrapping). Partial values are merged over the current ones. */
export function setRuntimeConfig(config: Partial<ZagruRuntimeConfig>): void {
  programmatic = deepMerge(programmatic ?? {}, config);
  memoized = null;
}

/** Clears programmatic overrides and the memoized value. */
export function resetRuntimeConfig(): void {
  programmatic = null;
  memoized = null;
}
